// 路徑：src/composables/useDashboardSignalR.ts
import { ref, onMounted, onUnmounted } from 'vue'
import * as signalR from '@microsoft/signalr'
import { ensureAccessToken } from '@/utils/authSession'

export function useDashboardSignalR(onRefreshCallback: () => void) {
  const connection = ref<signalR.HubConnection | null>(null)
  const isConnected = ref(false)

  // 根據環境動態決定 SignalR Hub 的網址
  // 如果前端有設定 vite proxy，可以直接寫 '/dashboardHub'
  // 否則本機開發對接後端 https://localhost:7261/dashboardHub
  const rawBaseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin
  const baseUrl = rawBaseUrl.replace(/\/+$/, '')

  const hubUrl = `${baseUrl}/dashboardHub`

  const initSignalR = async () => {
    // 1. 配置連線參數
    connection.value = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: true,
        accessTokenFactory: async () => (await ensureAccessToken()) ?? '',
      })
      // 🌟 開啟自動重連機制（斷線時會自動在 0, 2, 10, 30 秒後嘗試重連）
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    // 2. 監聽後端傳來的事件
    // 名稱必須與後端 SendAsync("RefreshDashboardStats") 完全一致
    connection.value.on('RefreshDashboardStats', () => {
      // 🌟 執行外頭傳進來的更新發放 (例如 Pinia 撈取最新數字的方法)
      onRefreshCallback()
    })

    // 3. 啟動連線
    try {
      await connection.value.start()
      isConnected.value = true
    } catch (err) {
      console.error('❌ SignalR 連線失敗:', err)
      isConnected.value = false
    }

    // 監聽重連事件（選填，方便 Debug）
    connection.value.onreconnecting(() => {
      isConnected.value = false
    })
    connection.value.onreconnected(() => {
      isConnected.value = true
    })
  }

  onMounted(() => {
    initSignalR()
  })

  // 🌟 離開頁面時切斷連線，釋放伺服器資源
  onUnmounted(async () => {
    if (connection.value) {
      await connection.value.stop()
    }
  })

  return {
    isConnected,
  }
}
